import React, { useEffect, useState, useRef } from "react";
import { IoMdClose } from "react-icons/io";
import { FiPlus, FiCheck } from "react-icons/fi";
import { MdOutlineTableChart } from "react-icons/md";
import AudienceDataComponent from "./AddTable/Audiences";
import GenderInfo from "./AddTable/Genderinfo";
import BidStrategyType from "./AddTable/BidStrategyType";
import CustomTable from "./AddTable/CustomTable";
import CampaignType from "./AddTable/CampaignType";
import DeviceTable from "./AddTable/DeviceTable";
import BudgetTable from "./AddTable/BudgetTable";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";

const tableConfig = {
  audiences: { title: "Audience", component: AudienceDataComponent },
  genderInfo: { title: "Gender Info", component: GenderInfo },
  bidStrategy: { title: "Bid Strategy", component: BidStrategyType },
  group: { title: "Group Metrics", component: CustomTable },
  device: { title: "Device", component: DeviceTable },
  budgetTable: { title: "Monthly Budget", component: BudgetTable },
  campaignType: { title: "Campaign Type", component: CampaignType },
};

const AddTable = ({
  startDate,
  endDate,
  CampaignTable,
  setCardTotalFunction,
  customerId,
  initialDateRange,
  uniqueCategories,
  PageType,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const defaultTables = [];
  const [createdTables, setCreatedTables] = useState([]);

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest?.(".MuiPopover-root") &&
        !event.target.closest?.(".MuiMenu-root")
      ) {
        setShowDropdown(false);
        setSelectedOption("");
      }
    };

    if (showDropdown || selectedOption) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, selectedOption]);

  const updateTableVisibility = async (tableName, tableObj, isAdded) => {
    let newCount = tableObj.length <= 0 ? 0 : tableObj[0].count;
    if (isAdded) newCount = newCount + 1;
    else newCount = newCount - 1;
    try {
      await fetch("https://api.confidanto.com/table-visibility", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: localStorage.getItem("project_id"),
          customer_id: customerId,
          table_name: tableName,
          count: newCount,
          page_type: PageType,
        }),
      });
    } catch (err) {
      console.error(`Failed to update visibility for ${tableName}:`, err);
    } finally {
      fetchVisibilityTables();
    }
  };

  const fetchVisibilityTables = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await fetch(
        "https://api.confidanto.com/table-visibility/get",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: localStorage.getItem("project_id"),
            customer_id: localStorage.getItem("customer_id"),
            email: localStorage.getItem("email"),
            page_type: PageType,
          }),
        },
      );
      const data = await res.json();
      if (Array.isArray(data)) setCreatedTables(Array.from(new Set([...data])));
      else setCreatedTables(defaultTables);
    } catch (err) {
      setError("Failed to load saved tables.");
      setCreatedTables(defaultTables);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisibilityTables();
  }, [customerId]);

  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
  };

  const handleCancel = () => {
    setShowDropdown(false);
    setSelectedOption("");
  };

  const handleCreateClick = () => {
    if (selectedOption) {
      let obj = {
        project_id: localStorage.getItem("project_id"),
        customer_id: localStorage.getItem("customer_id"),
        table_name: selectedOption,
        count: 1,
        group_ids: [],
      };
      setCreatedTables([...createdTables, obj]);
      updateTableVisibility(
        selectedOption,
        createdTables.filter((item) => item.table_name === selectedOption),
        true,
      );
      setSelectedOption("");
      setShowDropdown(false);
    }
  };

  const handleCloseTable = async (tableName) => {
    const tableToUpdate = createdTables.find((t) => t.table_name === tableName);
    if (!tableToUpdate) return;
    setCreatedTables((prev) =>
      prev
        .map((t) =>
          t.table_name === tableName ? { ...t, count: t.count - 1 } : t,
        )
        .filter((t) => t.count > 0),
    );
    updateTableVisibility(
      tableName,
      createdTables.filter((item) => item.table_name === tableName),
      false,
    );
  };

  const pageTypeMap = {
    daily_reporting: "daily",
    weekly_reporting: "weekly",
    monthly_reporting: "monthly",
    project_details: null,
  };

  const totalTableCount = createdTables.reduce(
    (acc, table) => acc + (table.count || 0),
    0,
  );

  return (
    <div className="text-left flex flex-col items-start w-full print:block space-y-4">
      {/* Top Header Control Bar */}
      <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200 print:hidden">
        <div className="flex items-center gap-2">
          <MdOutlineTableChart className="text-blue-600 text-xl" />
          <h3 className="text-base font-semibold text-slate-800">
            Data Tables
          </h3>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-600 border border-blue-100">
            {totalTableCount}
          </span>
        </div>

        <div className="flex items-center flex-wrap gap-2.5">
          {!showDropdown ? (
            <button
              onClick={toggleDropdown}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 shadow-2xs transition"
            >
              <FiPlus className="text-sm" />
              <span>Add Table</span>
            </button>
          ) : (
            <div
              ref={dropdownRef}
              className="flex items-center flex-wrap gap-2 bg-white p-2 rounded-xl border border-slate-200 shadow-sm animate-fade-in"
            >
              <FormControl size="small" className="w-44">
                <InputLabel className="text-xs">Select Table Type</InputLabel>
                <Select
                  value={selectedOption}
                  label="Select Table Type"
                  onChange={(e) => handleOptionClick(e.target.value)}
                  sx={{
                    fontSize: "0.85rem",
                    height: 36,
                    borderRadius: "0.5rem",
                  }}
                >
                  {Object.keys(tableConfig).map((key) => (
                    <MenuItem key={key} value={key}>
                      {tableConfig[key].title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedOption && (
                <button
                  onClick={handleCreateClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 h-9 rounded-lg transition shadow-xs flex items-center gap-1.5"
                >
                  <FiCheck className="text-sm" />
                  Create
                </button>
              )}

              <button
                type="button"
                onClick={handleCancel}
                title="Close"
                className="w-9 h-9 flex items-center justify-center text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-lg transition shadow-xs"
                aria-label="Close select"
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tables List */}
      {loading ? (
        <div className="py-8 flex items-center gap-2 text-slate-500">
          <CircularProgress size={20} className="text-blue-600" />
          <span className="text-sm font-medium">Loading saved tables...</span>
        </div>
      ) : error ? (
        <p className="text-gray-500 py-4">{error}</p>
      ) : totalTableCount === 0 ? (
        <div className="w-full py-8 border-2 border-dashed border-slate-200/90 rounded-2xl flex flex-col items-center justify-center text-center bg-white/40 print:hidden">
          <MdOutlineTableChart className="text-slate-300 text-3xl mb-2" />
          <p className="text-xs font-semibold text-slate-600">
            No tables added yet
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Click "+ Add Table" above to add table data.
          </p>
        </div>
      ) : (
        createdTables.map((table) => {
          if (!table.count || table.count < 1) return null;
          return Array.from({ length: table.count }).map((_, instanceIndex) => {
            const config = tableConfig[table.table_name];
            if (!config) return null;
            const TableComponent = config.component;
            const uniqueKey = `${table.table_name}-${instanceIndex}`;

            return (
              <div
                key={uniqueKey}
                className="mt-6 w-full max-w-9xl print:block print:break-inside-avoid print:page-break-inside-avoid print:mt-4"
              >
                <div className="flex items-center justify-between my-2">
                  <h2 className="text-lg font-semibold text-blue-600">
                    {config.title}
                  </h2>
                  <button
                    className="p-1 text-md bg-gray-400 hover:bg-gray-500 text-white rounded-sm print:hidden"
                    onClick={() => handleCloseTable(table.table_name)}
                  >
                    <IoMdClose />
                  </button>
                </div>
                {["campaignType", "bidStrategy"].includes(table.table_name) ? (
                  <TableComponent
                    customerId={customerId}
                    initialDateRange={initialDateRange}
                    uniqueCategories={uniqueCategories}
                    PageType={pageTypeMap[PageType]}
                  />
                ) : (
                  <TableComponent
                    startDate={startDate}
                    endDate={endDate}
                    initialDateRange={initialDateRange}
                    PageType={pageTypeMap[PageType]}
                  />
                )}
              </div>
            );
          });
        })
      )}
    </div>
  );
};

export default AddTable;
